import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import ClickOutside from 'vue-click-outside'

import { Button } from '@src/elements/button/button'
import { Icon } from '@src/elements/icon/icon'
import { SUPPORTED_IMAGE_TYPES } from '@src/util/constants'
import { Input } from '@src/elements/input/input'
import WorkoutData from '@src/data/workout-data'
import { validateSupportedFileType } from '@src/util/validation'
import { Modal } from '@src/elements/modal/modal'
import ClassData from '@src/data/class'

import '@src/routes/content/components/workouts/preview-image.sass'

export type PreviewImageDTO = {
  content: string
  filename: string
  content_type: string
}

const SUPPORTED_IMAGE_FORMATS = SUPPORTED_IMAGE_TYPES.map((item) => item.toUpperCase()).join(', ')
const RECOMMENDED_RESOLUTION = {
  width: 1080,
  height: 612,
  dpi: 72
}

const DEFAULT_PREVIEW_COMMON_STRING = 'default-workout'

@Component({
  directives: {
    ClickOutside
  }
})
export class PreviewImage extends Vue {

  @Prop({ required: true })
  private item: WorkoutData | ClassData

  @Prop({ required: true })
  private saveCallback: (previewImage?) => void

  private currentImage: PreviewImageDTO
  private selectedImage: PreviewImageDTO
  private uploadedImage: PreviewImageDTO
  private readonly title: string = ''

  constructor () {
    super()

    this.currentImage = {
      content: this.item.previewImage,
      filename: this.item.previewImage.split('/')[this.item.previewImage.split('/').length - 1],
      content_type: 'image'
    }
    this.selectedImage = this.uploadedImage || this.currentImage
    this.title = this.item instanceof WorkoutData ? 'PAGE.CONTENT.WORKOUT.SELECT_PREVIEW_IMAGE' : 'PAGE.CONTENT.CLASS.SELECT_PREVIEW_IMAGE'
  }

  resetToDefault () {
    if (this.item.id) {
      this.selectedImage = {
        content: `/assets/icon/brand/default-workout/${this.item.id % 30}.jpg`,
        filename: `${this.item.id % 30}.jpg`,
        content_type: 'image'
      }
    }
  }

  resetToCurrentImagePreview () {
    this.selectedImage = this.currentImage
  }

  selectUploadedImage () {
    if (this.uploadedImage) {
      this.selectedImage = this.uploadedImage
    }
  }

  save () {

    if (this.currentImage === this.selectedImage) {
      this.saveCallback()
      return
    }

    if (this.selectedImage!.content!.includes(DEFAULT_PREVIEW_COMMON_STRING)) {
      this.saveCallback(null)
      return
    }

    this.saveCallback({
      content: this.selectedImage!.content.split(',')[1],
      filename: this.selectedImage!.filename,
      content_type: this.selectedImage!.content_type
    })
  }

  onFileChange (e) {
    let files = e.target.files || e.dataTransfer.files
    if (!files.length) {
      return
    }

    // build the object to be displayed
    let previewImageDTO: PreviewImageDTO = {
      content: '',
      filename: files[0].name,
      content_type: files[0].type
    }

    // validate file format
    let hasFileError = !validateSupportedFileType(files[0].type, SUPPORTED_IMAGE_TYPES)

    if (hasFileError) {
      this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUT.PREVIEW_IMAGE_ERROR'), fadeOut: false })

    } else {
      this.$store.dispatch('closeToast')

      let reader = new FileReader()
      reader.readAsDataURL(files[0])

      reader.onload = () => {
        // image as base64
        const buffer = reader.result
        previewImageDTO.content = buffer as string
        this.uploadedImage = previewImageDTO
        this.selectUploadedImage()
      }
    }
  }

  render (h: CreateElement) {
    return <Modal show={true} title={this.$t(this.title)} closeCallback={() => this.saveCallback()} cssClass='preview-image-selector'>
      <div class='image-requirements'>
        <div>
          {this.$t('IMAGE_REQUIREMENTS.RECOMMENDED_IMAGE_RESOLUTION_TEXT')}&nbsp;
          <b>{this.$t('IMAGE_REQUIREMENTS.RECOMMENDED_IMAGE_RESOLUTION_VALUE', {
            width: RECOMMENDED_RESOLUTION.width,
            height: RECOMMENDED_RESOLUTION.height,
            dpi: RECOMMENDED_RESOLUTION.dpi
          })}</b>
        </div>
        <div>{this.$t('IMAGE_REQUIREMENTS.SUPPORTED_FILE_FORMATS_TEXT')}&nbsp; <b>{SUPPORTED_IMAGE_FORMATS}</b></div>
      </div>
      <div class='list'>
        {this.renderDefault(h)}
        {this.renderCurrentPreviewImage(h)}
        {this.renderSelectedForUpload(h)}
        {this.renderUploadForm(h)}
      </div>
      <div class='btn-row' slot='footer'>
        <Button type='outline' label={this.$t('CANCEL')} click={() => this.saveCallback()} />
        <Button class='save-btn' label={this.$t('SAVE')} click={this.save} />
      </div>
    </Modal>
  }

  renderDefault (h: CreateElement) {
    if (!this.item.id) return

    return this.renderPreview(
      h,
      `${this.item.id % 30}.jpg`,
      `/assets/icon/brand/default-workout/${this.item.id % 30}.jpg`,
      this.selectedImage.content.includes(DEFAULT_PREVIEW_COMMON_STRING),
      this.resetToDefault
    )
  }

  // Image/video currently set
  renderCurrentPreviewImage (h: CreateElement) {
    if (this.item.previewImage.includes(DEFAULT_PREVIEW_COMMON_STRING)) {
      return
    }

    return this.renderPreview(
      h,
      this.currentImage.filename,
      this.currentImage.content,
      this.selectedImage.content === this.currentImage.content,
      this.resetToCurrentImagePreview
    )
  }

  // Image/video selectedImage for upload
  renderSelectedForUpload (h: CreateElement) {
    if (!this.uploadedImage || this.uploadedImage.content.includes(DEFAULT_PREVIEW_COMMON_STRING)) {
      return
    }

    return this.renderPreview(
      h,
      this.uploadedImage.filename,
      this.uploadedImage.content,
      this.selectedImage.content === this.uploadedImage.content,
      this.selectUploadedImage)
  }

  // render the previewImage preview
  renderPreview (h: CreateElement, fileName: any, source: string, isSelected: boolean, selectCallback: () => void) {
    return <div class='image-preview' onClick={selectCallback}>
      <img src={source} />
      {isSelected && <Icon class='selected-btn' name='/util/selected' /> }
    </div>
  }

  // Button to select file from disk
  renderUploadForm (h: CreateElement) {
    return <div class='upload-form'>
      <Icon name='util/add' />
      <Input ref='file-upload' htmlId='previewImage' type='file' fileChanged={this.onFileChange} accept='image/*'/>
    </div>
  }

  beforeDestroy () {
    this.$store.dispatch('closeToast')
  }
}
